import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
    downloadEngineerStatisticsExport,
    loadCurrentUser,
    loadEngineerStatistics,
    loadEngineerStatisticsDetail,
    loadMetadata,
} from './api';
import SearchableSelect from './SearchableSelect';
import { WeakPerformanceHistoryModal, WeakStatusButton } from './WeakPerformanceHistoryModal';


function toDateInput(value) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function relativeDate(days) {
    const value = new Date();
    value.setDate(value.getDate() + days);
    return toDateInput(value);
}

function formatDate(value) {
    if (!value) {
        return '—';
    }
    return new Intl.DateTimeFormat('ru-RU').format(new Date(`${value}T00:00:00`));
}

const today = toDateInput(new Date());
const monthStart = `${today.slice(0, 8)}01`;
const monthEnd = (() => {
    const current = new Date();
    return new Date(current.getFullYear(), current.getMonth() + 1, 0).toISOString().slice(0, 10);
})();
const initialFilters = {
    start_date: monthStart,
    end_date: monthEnd,
    location: '',
};

const fallbackLocations = [
    { id: 1, city: 'Тюмень', address: 'Локальный пример' },
    { id: 2, city: 'Москва', address: 'Локальный пример' },
];

const fallbackStats = {
    period: {
        start_date: monthStart,
        end_date: monthEnd,
        location: '',
        work_days: 21,
        elapsed_work_days: 15,
        remaining_work_days: 6,
    },
    summary: {
        engineer_count: 3,
        completed_total: 58,
        team_avg_per_day: 3.2,
        lagging_systems_count: 2,
        required_per_day_total: 8.4,
    },
    weak_engineer: {
        engineer: 'Тестов Тест',
        location: 'Тюмень',
        avg_per_day: 1.9,
        days: 11,
        total: 21,
        gap_to_team_avg: -1.3,
        needs_attention: true,
    },
    worst_system: {
        system: '5X',
        model: 'BRM5X',
        daily_target: 4.5,
        daily_fact: 3.1,
        required_per_day_remaining: 6.2,
        pace_status: 'Нужно ускориться',
        needs_attention: true,
    },
    consolidated_data: [
        {
            engineer: 'Итоги',
            location: 'Все',
            total: 58,
            days: 18,
            avg_per_day: 3.2,
            '5X': 18,
            '2X': 16,
            'TE 2X': 14,
            'CIT 5X': 6,
            'CIT 2X': 4,
        },
        {
            engineer: 'Иванов И.И.',
            location: 'Тюмень',
            total: 21,
            days: 10,
            avg_per_day: 2.1,
            '5X': 8,
            '2X': 6,
            'TE 2X': 4,
            'CIT 5X': 2,
            'CIT 2X': 1,
        },
    ],
    system_stats: [
        {
            type: 'Modules',
            model: 'BRM5X',
            system: '5X',
            plan: 24,
            completed: 18,
            remaining: 6,
            status: 'Bad',
            plan_completion: 75,
            lag: -0.29,
            daily_target: 1.14,
            daily_fact: 0.93,
            required_per_day_remaining: 1.5,
            pace_status: 'Нужно ускориться',
            needs_attention: true,
            priority: 3,
        },
    ],
};


function formatValue(value, suffix = '') {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    return `${value}${suffix}`;
}

function NumberCell({ value }) {
    return <span className="shell-cellNumber">{value}</span>;
}

function StatsRow({ row, isTotals, isWeak, onOpenHistory, onOpenDetail }) {
    const className = [
        'shell-tableRow',
        isTotals ? 'shell-tableRow--totals' : '',
        isWeak?.persistent ? 'shell-tableRow--danger' : isWeak ? 'shell-tableRow--warning' : '',
    ].filter(Boolean).join(' ');

    return (
        <tr className={className}>
            <td>
                <div className="shell-identityCell">
                    {isTotals ? (
                        <strong>{row.engineer}</strong>
                    ) : (
                        <button
                            type="button"
                            className="shell-engineerDrilldownButton"
                            onClick={() => onOpenDetail?.(row)}
                            title={`Открыть выполненные работы: ${row.engineer}`}
                        >
                            {row.engineer}
                            <span aria-hidden="true">→</span>
                        </button>
                    )}
                    {isWeak ? <WeakStatusButton row={isWeak.row || isWeak} onClick={onOpenHistory} /> : null}
                </div>
            </td>
            <td>{row.location}</td>
            <td>{row.total}</td>
            <td>{row.days}</td>
            <td>{row.avg_per_day}</td>
            <td>{row['5X']}</td>
            <td>{row['2X']}</td>
            <td>{row['TE 2X']}</td>
            <td>{row['CIT 5X']}</td>
            <td>{row['CIT 2X']}</td>
        </tr>
    );
}

const emptyDetail = {
    summary: {
        total: 0,
        modules: 0,
        cassettes: 0,
        work_days: 0,
        by_system: {},
    },
    daily: [],
    items: [],
};

function EngineerDetailModal({ row, onClose }) {
    const initialPeriod = useMemo(() => ({
        start_date: today,
        end_date: today,
    }), []);
    const [draftPeriod, setDraftPeriod] = useState(initialPeriod);
    const [period, setPeriod] = useState(initialPeriod);
    const [category, setCategory] = useState('all');
    const [state, setState] = useState({
        data: emptyDetail,
        loading: true,
        error: '',
    });

    useEffect(() => {
        let cancelled = false;

        setState((current) => ({ ...current, loading: true, error: '' }));
        loadEngineerStatisticsDetail({
            engineer: row.engineer,
            location: row.location,
            ...period,
        })
            .then((payload) => {
                if (!cancelled) {
                    setState({ data: payload, loading: false, error: '' });
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setState({
                        data: emptyDetail,
                        loading: false,
                        error: 'Не удалось загрузить выполненные работы. Попробуйте обновить период.',
                    });
                }
            });

        return () => {
            cancelled = true;
        };
    }, [period, row.engineer, row.location]);

    const visibleItems = useMemo(
        () => state.data.items.filter((item) => category === 'all' || item.category === category),
        [category, state.data.items],
    );

    const quickPeriods = [
        { label: 'Сегодня', start_date: today, end_date: today },
        { label: 'Вчера', start_date: relativeDate(-1), end_date: relativeDate(-1) },
        { label: '7 дней', start_date: relativeDate(-6), end_date: today },
        { label: 'Текущий месяц', start_date: monthStart, end_date: today },
    ];

    function applyPeriod(nextPeriod) {
        setDraftPeriod(nextPeriod);
        setPeriod(nextPeriod);
    }

    function submitPeriod(event) {
        event.preventDefault();
        if (!draftPeriod.start_date || !draftPeriod.end_date || draftPeriod.start_date > draftPeriod.end_date) {
            setState((current) => ({
                ...current,
                error: 'Проверьте даты: начало периода должно быть не позже окончания.',
            }));
            return;
        }
        setPeriod(draftPeriod);
    }

    return (
        <div className="shell-modalBackdrop" onClick={onClose}>
            <div className="shell-modalCard shell-modalCard--wide" onClick={(event) => event.stopPropagation()}>
                <div className="shell-modalHeader">
                    <div>
                        <span className="shell-sectionEyebrow">Выполненные работы</span>
                        <h3>{row.engineer}</h3>
                        <p className="shell-cardHint">{row.location}</p>
                    </div>
                    <button type="button" className="shell-modalClose" onClick={onClose}>
                        Закрыть
                    </button>
                </div>

                <div className="shell-modalBody shell-engineerDetail">
                    <div className="shell-tabRow shell-engineerDetailQuickPeriods">
                        {quickPeriods.map((item) => {
                            const isActive = period.start_date === item.start_date && period.end_date === item.end_date;
                            return (
                                <button
                                    key={item.label}
                                    type="button"
                                    className={`shell-tabButton ${isActive ? 'is-active' : ''}`.trim()}
                                    onClick={() => applyPeriod({
                                        start_date: item.start_date,
                                        end_date: item.end_date,
                                    })}
                                >
                                    {item.label}
                                </button>
                            );
                        })}
                    </div>

                    <form className="shell-engineerDetailPeriod" onSubmit={submitPeriod}>
                        <label className="shell-field">
                            <span>Дата начала</span>
                            <input
                                type="date"
                                value={draftPeriod.start_date}
                                onChange={(event) => setDraftPeriod((current) => ({
                                    ...current,
                                    start_date: event.target.value,
                                }))}
                            />
                        </label>
                        <label className="shell-field">
                            <span>Дата окончания</span>
                            <input
                                type="date"
                                value={draftPeriod.end_date}
                                onChange={(event) => setDraftPeriod((current) => ({
                                    ...current,
                                    end_date: event.target.value,
                                }))}
                            />
                        </label>
                        <button
                            type="submit"
                            className="shell-actionButton shell-actionButton--primary shell-actionButton--small"
                            disabled={state.loading}
                        >
                            {state.loading ? 'Загрузка...' : 'Показать'}
                        </button>
                    </form>

                    {state.error ? <div className="shell-banner">{state.error}</div> : null}

                    <div className="shell-pageSummary shell-pageSummary--engineerDetail">
                        <div className="shell-pageSummaryCard shell-pageSummaryCard--compact">
                            <span>Всего выполнено</span>
                            <strong className="shell-cellNumber">{state.loading ? '...' : state.data.summary.total}</strong>
                        </div>
                        <div className="shell-pageSummaryCard shell-pageSummaryCard--compact">
                            <span>Модулей</span>
                            <strong className="shell-cellNumber">{state.loading ? '...' : state.data.summary.modules}</strong>
                        </div>
                        <div className="shell-pageSummaryCard shell-pageSummaryCard--compact">
                            <span>Кассет</span>
                            <strong className="shell-cellNumber">{state.loading ? '...' : state.data.summary.cassettes}</strong>
                        </div>
                        <div className="shell-pageSummaryCard shell-pageSummaryCard--compact">
                            <span>Дней с результатом</span>
                            <strong className="shell-cellNumber">{state.loading ? '...' : state.data.summary.work_days}</strong>
                        </div>
                    </div>

                    {state.data.daily.length > 1 ? (
                        <div className="shell-engineerDaily">
                            <h4>По дням</h4>
                            <div className="shell-engineerDailyGrid">
                                {state.data.daily.map((item) => (
                                    <button
                                        type="button"
                                        key={item.date}
                                        className="shell-engineerDailyCard"
                                        onClick={() => applyPeriod({ start_date: item.date, end_date: item.date })}
                                    >
                                        <span>{formatDate(item.date)}</span>
                                        <strong>{item.total}</strong>
                                        <small>{item.modules} мод. · {item.cassettes} кас.</small>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    <div className="shell-tabRow shell-engineerDetailCategories">
                        {[
                            { value: 'all', label: `Все (${state.data.summary.total})` },
                            { value: 'modules', label: `Модули (${state.data.summary.modules})` },
                            { value: 'cassettes', label: `Кассеты (${state.data.summary.cassettes})` },
                        ].map((item) => (
                            <button
                                key={item.value}
                                type="button"
                                className={`shell-tabButton ${category === item.value ? 'is-active' : ''}`.trim()}
                                onClick={() => setCategory(item.value)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>

                    <div className="shell-tableWrap shell-tableWrap--modal shell-tableWrap--engineerDetail">
                        <table className="shell-table shell-table--engineerDetail">
                            <thead>
                                <tr>
                                    <th>Дата</th>
                                    <th>Тип</th>
                                    <th>Система</th>
                                    <th>Серийный номер</th>
                                    <th>Партия</th>
                                    <th>Территория</th>
                                    <th>Время</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleItems.map((item) => (
                                    <tr key={item.id} className="shell-tableRow">
                                        <td>{formatDate(item.date_repair)}</td>
                                        <td>{item.category_label}</td>
                                        <td>{item.system}</td>
                                        <td>
                                            <Link className="shell-engineerDetailSerial" to={`/app/search/${item.id}`}>
                                                {item.serial_number}
                                            </Link>
                                        </td>
                                        <td>{item.party_number || '—'}</td>
                                        <td>{item.territory || '—'}</td>
                                        <td>{item.time_spent_minutes ? `${item.time_spent_minutes} мин` : '—'}</td>
                                    </tr>
                                ))}
                                {!state.loading && !visibleItems.length ? (
                                    <tr>
                                        <td colSpan="7">
                                            <div className="shell-emptyState shell-emptyState--compact">
                                                За выбранный период выполненных работ нет.
                                            </div>
                                        </td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

function WeakCombinedTable({ rows, onOpenHistory, onOpenDetail }) {
    return (
        <section className="shell-section">
            <div className="shell-sectionHeader">
                <div>
                    <span className="shell-sectionEyebrow">Контроль темпа</span>
                    <h3>Кому нужно нарастить темп</h3>
                </div>
                <span>{rows.length ? `${rows.length} строк` : 'Нет записей'}</span>
            </div>

            {rows.length ? (
                <div className="shell-tableWrap shell-tableWrap--weakSummary">
                    <table className="shell-table shell-table--compactSummary">
                        <thead>
                            <tr>
                                <th>Лок.</th>
                                <th>Инженер</th>
                                <th>Тип</th>
                                <th>Ср./день</th>
                                <th>Норма/мес.</th>
                                <th>Метка</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((item) => (
                                <tr
                                    key={`${item.category}-${item.location}-${item.engineer}`}
                                    className={`shell-tableRow ${item.status === 'persistent' ? 'shell-tableRow--danger' : 'shell-tableRow--warning'}`.trim()}
                                >
                                    <td>{item.location}</td>
                                    <td className="shell-tableCell--engineerName" title={item.engineer}>
                                        <button
                                            type="button"
                                            className="shell-engineerDrilldownButton"
                                            onClick={() => onOpenDetail?.({
                                                engineer: item.engineer,
                                                location: item.location,
                                            })}
                                        >
                                            {item.engineer}
                                            <span aria-hidden="true">→</span>
                                        </button>
                                    </td>
                                    <td>{item.category === 'modules' ? 'Модули' : 'Кассеты'}</td>
                                    <td className="shell-tableCell--pace">
                                        <strong className="shell-cellNumber">{item.avg_per_day}</strong>
                                        <span>порог {formatValue(item.weak_threshold)}</span>
                                    </td>
                                    <td><NumberCell value={item.monthly_norm_units} /></td>
                                    <td>
                                        <WeakStatusButton row={item} onClick={onOpenHistory} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="shell-emptyState shell-emptyState--compact">Недостаточно данных для сравнения.</div>
            )}

        </section>
    );
}


export default function EngineerStatisticsPage() {
    const [filters, setFilters] = useState(initialFilters);
    const [locations, setLocations] = useState(fallbackLocations);
    const [state, setState] = useState({
        data: fallbackStats,
        loading: true,
        warning: '',
    });
    const [exportLoading, setExportLoading] = useState(false);
    const [selectedWeakRow, setSelectedWeakRow] = useState(null);
    const [selectedEngineer, setSelectedEngineer] = useState(null);

    useEffect(() => {
        let cancelled = false;

        async function bootstrap() {
            try {
                const [metadata, currentUser] = await Promise.all([
                    loadMetadata(),
                    loadCurrentUser(),
                ]);

                const defaultLocation = currentUser?.location?.city || '';
                const appliedFilters = {
                    ...initialFilters,
                    location: defaultLocation,
                };
                const payload = await loadEngineerStatistics(appliedFilters);

                if (cancelled) {
                    return;
                }

                setLocations(metadata.locations || fallbackLocations);
                setFilters(appliedFilters);
                setState({
                    data: payload,
                    loading: false,
                    warning: '',
                });
            } catch (_error) {
                if (cancelled) {
                    return;
                }

                setLocations(fallbackLocations);
                setState({
                    data: fallbackStats,
                    loading: false,
                    warning: 'Не удалось загрузить статистику. Показан локальный пример.',
                });
            }
        }

        bootstrap();

        return () => {
            cancelled = true;
        };
    }, []);

    async function submitFilters(event) {
        event.preventDefault();
        setState((current) => ({
            ...current,
            loading: true,
        }));

        try {
            const payload = await loadEngineerStatistics(filters);
            setState({
                data: payload,
                loading: false,
                warning: '',
            });
        } catch (_error) {
            setState({
                data: fallbackStats,
                loading: false,
                warning: 'Не удалось загрузить статистику. Показан локальный пример.',
            });
        }
    }

    async function handleExport() {
        setExportLoading(true);
        try {
            await downloadEngineerStatisticsExport(filters);
        } finally {
            setExportLoading(false);
        }
    }

    const weakEngineer = state.data.weak_engineer;
    const weakModulesByLocation = useMemo(
        () => state.data.weak_performance?.modules_by_location || state.data.weak_engineers_by_location || (weakEngineer ? [weakEngineer] : []),
        [state.data.weak_performance, state.data.weak_engineers_by_location, weakEngineer],
    );
    const weakCassettesByLocation = useMemo(
        () => state.data.weak_performance?.cassettes_by_location || [],
        [state.data.weak_performance],
    );
    const worstSystem = state.data.worst_system;
    const weakRowMap = useMemo(() => {
        const map = new Map();
        [...weakModulesByLocation, ...weakCassettesByLocation].forEach((item) => {
            const key = `${item.engineer}::${item.location}`;
            const current = map.get(key);
            const next = {
                label: item.label,
                persistent: item.status === 'persistent' || current?.persistent,
                row: item.status === 'persistent' || !current?.row ? item : current.row,
            };
            if (!current || next.persistent || !current.persistent) {
                map.set(key, next);
            }
        });
        return map;
    }, [weakModulesByLocation, weakCassettesByLocation]);
    const weakRows = useMemo(
        () => [...weakModulesByLocation, ...weakCassettesByLocation].sort((left, right) => {
            const leftPersistent = left.status === 'persistent' ? 0 : 1;
            const rightPersistent = right.status === 'persistent' ? 0 : 1;
            if (leftPersistent !== rightPersistent) {
                return leftPersistent - rightPersistent;
            }
            if (left.location !== right.location) {
                return left.location.localeCompare(right.location, 'ru');
            }
            return String(left.avg_per_day).localeCompare(String(right.avg_per_day), 'ru', { numeric: true });
        }),
        [weakModulesByLocation, weakCassettesByLocation],
    );

    const summaryCards = useMemo(() => ([
        { label: 'Инженеров в выборке', value: state.loading ? '...' : state.data.summary.engineer_count },
        { label: 'Всего закрыто', value: state.loading ? '...' : state.data.summary.completed_total },
        { label: 'Средний темп команды', value: state.loading ? '...' : `${state.data.summary.team_avg_per_day} / день` },
        { label: 'Осталось рабочих дней', value: state.loading ? '...' : state.data.period.remaining_work_days },
        { label: 'Систем с просадкой', value: state.loading ? '...' : state.data.summary.lagging_systems_count },
        { label: 'Нужно в день, всего', value: state.loading ? '...' : `${state.data.summary.required_per_day_total} ед` },
    ]), [state.data, state.loading]);

    return (
        <div className="shell-page">
            <section className="shell-section shell-section--pageTop">
                <div className="shell-pageHeaderCompact">
                    <div className="shell-pageBandIntro">
                        <h3>Статистика инженеров</h3>
                    </div>

                    <div className="shell-pageSummary shell-pageSummary--header shell-pageSummary--metricsWide">
                        {summaryCards.map((item) => (
                            <div key={item.label} className="shell-pageSummaryCard shell-pageSummaryCard--compact">
                                <span>{item.label}</span>
                                <strong className="shell-cellNumber">{item.value}</strong>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {state.warning ? <div className="shell-banner">{state.warning}</div> : null}

            <section className="shell-section">
                <div className="shell-sectionHeader">
                    <div>
                        <span className="shell-sectionEyebrow">Фильтры</span>
                        <h3>Период и локация</h3>
                    </div>
                </div>

                <form className="shell-searchForm shell-searchForm--compact" onSubmit={submitFilters}>
                    <label className="shell-field">
                        <span>Дата начала</span>
                        <input
                            type="date"
                            value={filters.start_date}
                            onChange={(event) => setFilters((current) => ({ ...current, start_date: event.target.value }))}
                        />
                    </label>
                    <label className="shell-field">
                        <span>Дата окончания</span>
                        <input
                            type="date"
                            value={filters.end_date}
                            onChange={(event) => setFilters((current) => ({ ...current, end_date: event.target.value }))}
                        />
                    </label>
                    <label className="shell-field">
                        <span>Локация</span>
                        <SearchableSelect
                            value={filters.location}
                            onChange={(value) => setFilters((current) => ({ ...current, location: value }))}
                            options={locations.map((item) => ({
                                value: item.city,
                                label: item.city,
                            }))}
                            placeholder="Все"
                        />
                    </label>
                    <div className="shell-searchActions">
                        <button type="submit" className="shell-actionButton shell-actionButton--primary">
                            {state.loading ? 'Обновляем...' : 'Обновить'}
                        </button>
                        <button
                            type="button"
                            className="shell-actionButton shell-actionButton--secondary"
                            onClick={handleExport}
                            disabled={exportLoading}
                        >
                            {exportLoading ? 'Готовим Excel...' : 'Экспорт в Excel'}
                        </button>
                    </div>
                </form>
            </section>

            <WeakCombinedTable
                rows={weakRows}
                onOpenHistory={setSelectedWeakRow}
                onOpenDetail={setSelectedEngineer}
            />

            <section className="shell-section shell-section--statsFocus">
                <div className="shell-sectionHeader">
                    <div>
                        <span className="shell-sectionEyebrow">План</span>
                        <h3>Главная просадка плана</h3>
                    </div>
                </div>

                {worstSystem ? (
                    <article className={`shell-planFocusCard ${worstSystem.needs_attention ? 'shell-planFocusCard--warning' : ''}`}>
                        <div className="shell-planFocusHeader">
                            <div>
                                <strong>{worstSystem.system}</strong>
                                <span>{worstSystem.type === 'Modules' ? 'Модули' : 'Кассеты'}</span>
                            </div>
                            <span className={`shell-resultBadge ${worstSystem.needs_attention ? 'shell-resultBadge--danger' : 'shell-resultBadge--teal'}`}>
                                {worstSystem.pace_status}
                            </span>
                        </div>
                        <div className="shell-planFocusMeta">
                            <div><span>Осталось</span><strong><NumberCell value={worstSystem.remaining} /></strong></div>
                            <div><span>Факт/день</span><strong><NumberCell value={worstSystem.daily_fact} /></strong></div>
                            <div><span>Нужно/день</span><strong><NumberCell value={worstSystem.required_per_day_remaining} /></strong></div>
                            <div><span>Раб. дней</span><strong><NumberCell value={state.data.period.remaining_work_days} /></strong></div>
                        </div>
                    </article>
                ) : (
                    <div className="shell-emptyState shell-emptyState--compact">Системы без критической просадки.</div>
                )}
            </section>

            <section className="shell-section">
                <div className="shell-sectionHeader">
                    <div>
                        <span className="shell-sectionEyebrow">Сводка инженеров</span>
                        <h3>Кто тянет команду, а кто тормозит общий результат</h3>
                    </div>
                    <span>{state.loading ? 'Загрузка...' : `${state.data.consolidated_data.length} строк`}</span>
                </div>

                <div className="shell-tableWrap">
                    <table className="shell-table">
                        <thead>
                            <tr>
                                <th>Инженер</th>
                                <th>Локация</th>
                                <th>Всего</th>
                                <th>Дней</th>
                                <th>Ср./день</th>
                                <th>5X</th>
                                <th>2X</th>
                                <th>TE 2X</th>
                                <th>CIT 5X</th>
                                <th>CIT 2X</th>
                            </tr>
                        </thead>
                        <tbody>
                            {state.data.consolidated_data.map((row, index) => {
                                const weakState = weakRowMap.get(`${row.engineer}::${row.location}`) || null;
                                return (
                                    <StatsRow
                                        key={`${row.engineer}-${row.location}-${index}`}
                                        row={row}
                                        isTotals={index === 0}
                                        isWeak={weakState}
                                        onOpenHistory={setSelectedWeakRow}
                                        onOpenDetail={setSelectedEngineer}
                                    />
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </section>

            <section className="shell-section">
                <div className="shell-sectionHeader">
                    <div>
                        <span className="shell-sectionEyebrow">План и темп</span>
                        <h3>Где нужно ускориться, чтобы не сорвать план</h3>
                    </div>
                    <span>{state.loading ? 'Загрузка...' : `${state.data.system_stats.length} систем`}</span>
                </div>

                <div className="shell-planGrid">
                    {state.data.system_stats.map((item) => (
                        <article key={item.system} className={`shell-planCard ${item.needs_attention ? 'shell-planCard--warning' : ''}`}>
                            <div className="shell-planCardTop">
                                <div>
                                    <h4>{item.system}</h4>
                                    <p>{item.type === 'Modules' ? 'Модули' : 'Кассеты'}</p>
                                </div>
                                <span className={`shell-resultBadge ${item.needs_attention ? 'shell-resultBadge--danger' : 'shell-resultBadge--teal'}`}>
                                    {item.pace_status}
                                </span>
                            </div>

                            <div className="shell-planMetrics">
                                <div><span>План</span><strong>{item.plan}</strong></div>
                                <div><span>Сделано</span><strong><NumberCell value={item.completed} /></strong></div>
                                <div><span>Осталось</span><strong><NumberCell value={item.remaining} /></strong></div>
                                <div><span>% плана</span><strong><NumberCell value={item.plan_completion} /></strong></div>
                                <div><span>План/день</span><strong><NumberCell value={item.daily_target} /></strong></div>
                                <div><span>Факт/день</span><strong><NumberCell value={item.daily_fact} /></strong></div>
                                <div><span>Нужно/день</span><strong><NumberCell value={item.required_per_day_remaining} /></strong></div>
                                <div><span>Приор.</span><strong><NumberCell value={item.priority} /></strong></div>
                            </div>
                        </article>
                    ))}
                </div>
            </section>

            <WeakPerformanceHistoryModal row={selectedWeakRow} onClose={() => setSelectedWeakRow(null)} />
            {selectedEngineer ? (
                <EngineerDetailModal row={selectedEngineer} onClose={() => setSelectedEngineer(null)} />
            ) : null}
        </div>
    );
}
